import { useCallback, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import { createTournament, listTables, slotConflictDetail } from '../../lib/api';
import { Button } from '../../components/Button';
import { SlotConflictBanner } from '../../components/SlotConflictBanner';
import { useToast } from '../../hooks/useToast';
import { PS, T } from '../../lib/theme';
import {
  EMPTY_TOURNAMENT_FORM,
  buildCreateTournamentBody,
  tournamentFormErrors,
  type TournamentFormValue,
} from '../../lib/tournament-form';
import type { SlotConflictDetail, TableEmbed } from '../../lib/types';
import { TournamentFieldsForm } from './TournamentFieldsForm';

export function NewTournamentPage() {
  const navigate = useNavigate();
  const { push } = useToast();

  const tablesLoader = useCallback((signal: AbortSignal) => listTables(signal), []);
  const tables = useApi<TableEmbed[]>(tablesLoader);

  const [form, setForm] = useState<TournamentFormValue>(EMPTY_TOURNAMENT_FORM);
  const [submitting, setSubmitting] = useState(false);
  // Kept as the whole 409 body so the banner can render `next_free_time` too.
  // Not cleared on every keystroke — only on a fresh submit attempt — so it
  // stays visible while the admin adjusts the form to resolve it.
  const [conflict, setConflict] = useState<SlotConflictDetail | null>(null);

  const patch = (p: Partial<TournamentFormValue>) => setForm((f) => ({ ...f, ...p }));

  const errors = tournamentFormErrors(form);
  const canSubmit = errors.length === 0 && !submitting;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setConflict(null);
    try {
      // Always created as a draft — publishing is a separate, explicit step
      // on the detail page once the admin has reviewed it.
      const body = buildCreateTournamentBody(form, 'draft');
      const tournament = await createTournament(body);
      push('success', `Tournament #${tournament.id} created as a draft.`);
      navigate(`/tournaments/${tournament.id}`);
    } catch (err) {
      // Structured 409 (api#155's `overlap`, the same #81 shape every other
      // slot-conflict surface uses) — narrow it and keep every field the
      // admin already filled in exactly as they left it. Anything else
      // (validation, admin_no_user_row, …) falls through to the toast.
      const d = slotConflictDetail(err);
      if (d) {
        setConflict(d);
        push('error', `${d.conflicts.length} conflict${d.conflicts.length === 1 ? '' : 's'} — resolve below and retry.`);
        return;
      }
      push('error', err instanceof Error ? err.message : 'Create failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 800 }}>
      <header>
        <h1 style={{ ...T.sectionTitle, margin: 0 }}>New tournament</h1>
        <p style={{ ...T.caption, color: PS.textSec, marginTop: 4 }}>
          Created as a draft. Publish it from the detail page once it&rsquo;s ready.
        </p>
      </header>

      <form
        onSubmit={(e) => void handleSubmit(e)}
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 20,
          background: PS.white,
          padding: 20,
          border: `1px solid ${PS.borderLight}`,
          borderRadius: 4,
        }}
      >
        <TournamentFieldsForm
          value={form}
          onChange={patch}
          tables={tables.data ?? []}
          tablesLoading={tables.loading}
        />

        {errors.length > 0 && (
          <ul style={{ ...T.caption, color: PS.errorText, margin: 0, paddingLeft: 18 }}>
            {errors.map((e) => (
              <li key={e}>{e}</li>
            ))}
          </ul>
        )}

        <div style={{ display: 'flex', gap: 8 }}>
          <Button type="submit" disabled={!canSubmit}>
            {submitting ? 'Creating…' : 'Create draft'}
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={() => navigate('/tournaments')}
            disabled={submitting}
          >
            Cancel
          </Button>
        </div>
      </form>

      {conflict && <SlotConflictBanner detail={conflict} />}
    </div>
  );
}
